import React, { createContext } from 'react';
import { useContext } from 'react';
import { useEffect, useState } from 'react';
import * as Sentry from '@sentry/browser';
import logger from '@spotify-internal/isomorphic-logger';
import {
  SilentAuthError,
  UserInfo,
} from '@spotify-internal/oauth-silent-authentication';
import {
  getSilentAuthToken,
  getUserInfo,
  login,
} from '@lib/auth/accessTokenManager/authActions';
import silentAuth from '@lib/auth/accessTokenManager/silentAuth';

export type UserContextValue = {
  loginState: LoginState;
  userInfo?: UserInfo;
};

export enum LoginState {
  AWAITING_LOGIN,
  LOGGEDIN,
  LOGIN_FAILED,
}

const getLoginState = async () => {
  try {
    const token = await getSilentAuthToken();
    if (token) {
      return LoginState.LOGGEDIN;
    }
    await login();
    return LoginState.AWAITING_LOGIN;
  } catch (error) {
    // error is a SilentAuthError
    if ('error' in error) {
      logger.error(
        `getLoginState() error: ${error.error}: ${error.error_description}`,
      );
      Sentry.captureException(
        new Error(`${error.error}: ${error.error_description}`),
      );
    } else {
      // error is an Error object, capture as regular error
      logger.error(error);
      Sentry.captureException(error);
    }
    return LoginState.LOGIN_FAILED;
  }
};

// exported for testing
export const UserContext = createContext<UserContextValue>({
  loginState: LoginState.AWAITING_LOGIN,
});

export const UserProvider = ({ children }: React.PropsWithChildren<{}>) => {
  const [userInfo, setUserInfo] = useState<UserInfo | undefined>(undefined);
  const [loginState, setLoginState] = useState<LoginState>(
    LoginState.AWAITING_LOGIN,
  );

  useEffect(() => {
    if (silentAuth && loginState === LoginState.AWAITING_LOGIN) {
      getLoginState()
        .then(state => {
          setLoginState(state);
          return state;
        })
        .then(state => {
          if (state === LoginState.LOGGEDIN) {
            return getUserInfo();
          }
          return undefined;
        })
        .then(user => setUserInfo(user))
        .catch((error: SilentAuthError) => {
          if ('error' in error) {
            logger.error(
              `getUserInfo() error: ${error.error}: ${error.error_description}`,
            );
            Sentry.captureException(
              new Error(`${error.error}: ${error.error_description}`),
            );
          } else {
            // error is an Error object, capture as regular error
            logger.error(error);
            Sentry.captureException(error);
          }
        });
    }
  }, [loginState]);

  return (
    <UserContext.Provider
      value={{
        userInfo,
        loginState,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  return useContext(UserContext);
};
